import { useCallback, useEffect, useId, useRef, useState } from 'react';

export interface PlaceResult {
  city: string;
  country: string;
  lat: number;
  lon: number;
  formattedAddress: string;
  /** Picked a whole city (not a neighbourhood, address or coordinates) */
  isCity?: boolean;
  /** Picked a whole country or state, or the whole world: mapped as an area rather than streets around a point */
  areaType?: 'world' | 'country' | 'administrative_area_level_1';
  areaName?: string;
  countryCode?: string;
}

/** The "Map the whole world" choice: the map-poster Lambda maps area_type "world" to its world map */
export const WORLD_PLACE: PlaceResult = {
  city: 'The World',
  country: '',
  lat: 0,
  lon: 0,
  formattedAddress: 'The whole world',
  areaType: 'world',
  areaName: 'World',
};

interface Prediction {
  placeId: string;
  description: string;
  mainText: string;
}

type InputMode = 'search' | 'coordinates';

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: PlaceResult) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Start with this place chosen (e.g. the world, from a landing page's "Map the Whole World") */
  initialPlace?: PlaceResult | null;
  /** The "Map the whole world" button and the coordinates toggle: off where only a place will do (labels) */
  showExtras?: boolean;
  label?: string;
  helperText?: string;
}

const DEBOUNCE_MS = 300;

export function PlaceAutocomplete({
  onPlaceSelect,
  disabled,
  placeholder = 'Search for a city, state or country...',
  initialPlace,
  showExtras = true,
  label = 'Location',
  helperText = 'Search for a city, neighbourhood, state or country, or map the whole world',
}: PlaceAutocompleteProps) {
  const [inputMode, setInputMode] = useState<InputMode>('search');
  const [inputValue, setInputValue] = useState(initialPlace?.formattedAddress ?? '');
  const [worldSelected, setWorldSelected] = useState(initialPlace?.areaType === 'world');
  const inputId = useId();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Coordinates mode state
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [coordsLoading, setCoordsLoading] = useState(false);
  const [coordsError, setCoordsError] = useState<string | null>(null);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const skipNextFetchRef = useRef(false);
  const lastGeocodedRef = useRef<string | null>(null);

  // Fetch predictions via backend proxy
  const fetchPredictions = useCallback(async (input: string) => {
    if (input.length < 2) {
      setPredictions([]);

      return;
    }

    setIsLoading(true);

    try {
      const url = `/api/places-autocomplete?input=${encodeURIComponent(input)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.predictions) {
        setPredictions(data.predictions);
        setShowDropdown(true);
      } else {
        setPredictions([]);
      }
    } catch (_err) {
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced input handler
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Skip fetch if input was set programmatically (from selection)
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    if (inputValue.length < 2) {
      setPredictions([]);
      setShowDropdown(false);

      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchPredictions(inputValue);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, fetchPredictions]);

  const selectWorld = () => {
    skipNextFetchRef.current = true;
    setInputValue(WORLD_PLACE.formattedAddress);
    setShowDropdown(false);
    setPredictions([]);
    setWorldSelected(true);
    onPlaceSelect(WORLD_PLACE);
  };

  // Handle place selection
  const handleSelect = useCallback(async (prediction: Prediction) => {
    skipNextFetchRef.current = true;
    setWorldSelected(false);
    setInputValue(prediction.description);
    setShowDropdown(false);
    setPredictions([]);
    setIsLoading(true);

    try {
      const url = `/api/places-autocomplete?placeId=${encodeURIComponent(prediction.placeId)}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.lat && data.lon) {
        onPlaceSelect({
          city: data.city,
          country: data.country,
          lat: data.lat,
          lon: data.lon,
          formattedAddress: data.formattedAddress,
          isCity: data.isCity,
          areaType: data.areaType,
          areaName: data.areaName,
          countryCode: data.countryCode,
        });
      }
    } catch (_err) {
      // Ignore errors
    } finally {
      setIsLoading(false);
    }
  }, [onPlaceSelect]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();

        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelect(predictions[selectedIndex]);
        }

        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reverse geocode coordinates to get city/country
  const reverseGeocode = useCallback(async (latNum: number, lonNum: number) => {
    setCoordsLoading(true);
    setCoordsError(null);

    try {
      const url = `/api/places-autocomplete?lat=${latNum}&lon=${lonNum}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.error) {
        setCoordsError(data.error);
        return;
      }

      onPlaceSelect({
        city: data.city || '',
        country: data.country || '',
        lat: latNum,
        lon: lonNum,
        formattedAddress: data.formattedAddress || '',
      });
    } catch (_err) {
      setCoordsError('Failed to fetch location details');
    } finally {
      setCoordsLoading(false);
    }
  }, [onPlaceSelect]);

  // Auto-fetch when coordinates are valid
  useEffect(() => {
    if (inputMode !== 'coordinates') return;

    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);

    if (
      isNaN(latNum) ||
      isNaN(lonNum) ||
      latNum < -90 ||
      latNum > 90 ||
      lonNum < -180 ||
      lonNum > 180
    ) {
      return;
    }

    // Skip if we've already geocoded these exact coordinates
    const coordKey = `${latNum},${lonNum}`;
    if (lastGeocodedRef.current === coordKey) {
      return;
    }

    // Debounce the reverse geocode call
    const timeout = setTimeout(() => {
      lastGeocodedRef.current = coordKey;
      reverseGeocode(latNum, lonNum);
    }, 500);

    return () => clearTimeout(timeout);
  }, [inputMode, lat, lon, reverseGeocode]);

  return (
    <div ref={containerRef} className="relative space-y-3">
      <div className="flex items-center justify-between">
        <label htmlFor={inputId} className="text-primary-2-80 block text-sm font-medium">
          {label}
        </label>
        {showExtras && (
          <button
            type="button"
            onClick={() => {
              setInputMode(inputMode === 'search' ? 'coordinates' : 'search');
              lastGeocodedRef.current = null;
            }}
            className="text-secondary-1 hover:text-primary-1-80 text-xs font-medium"
            disabled={disabled}
          >
            {inputMode === 'search' ? 'Use coordinates' : 'Search instead'}
          </button>
        )}
      </div>

      {inputMode === 'search' ? (
        <>
          <div className="relative">
            <input
              type="text"
              id={inputId}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setSelectedIndex(-1);
                setWorldSelected(false);
              }}
              onFocus={() => {
                if (predictions.length > 0) {
                  setShowDropdown(true);
                }
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={placeholder}
              className="border-neutral-2-20 focus:border-secondary-1 focus:ring-secondary-1 block w-full rounded-md border px-3 py-2 pr-10 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-2-10"
              autoComplete="off"
            />
            {isLoading && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="text-neutral-2-40 h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && predictions.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
              {predictions.map((prediction, index) => (
                <li
                  key={prediction.placeId}
                  onClick={() => handleSelect(prediction)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`cursor-pointer px-4 py-2 text-sm ${
                    index === selectedIndex ? 'bg-secondary-1 text-white' : 'text-neutral-2-80 hover:bg-neutral-2-10'
                  }`}
                >
                  <span className="font-medium">{prediction.mainText}</span>
                  {prediction.description !== prediction.mainText && (
                    <span className={index === selectedIndex ? 'text-white/70' : 'text-neutral-2-60'}>
                      {' '}
                      - {prediction.description.replace(prediction.mainText + ', ', '')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-neutral-2-60 text-xs">{helperText}</p>

          {showExtras && (
            <button
              type="button"
              onClick={selectWorld}
              disabled={disabled}
              aria-pressed={worldSelected}
              className={`flex w-full items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                worldSelected
                  ? 'border-secondary-1 bg-secondary-1 text-white'
                  : 'border-secondary-1/40 text-primary-2-80 hover:border-secondary-1 hover:bg-secondary-1/5'
              }`}
            >
              <svg className="h-7 w-7 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
              </svg>
              <span className="flex-1">
                <span className="block text-sm font-semibold">Map the whole world</span>
                <span className={`block text-xs ${worldSelected ? 'text-white/80' : 'text-neutral-2-60'}`}>
                  Every country on one puzzle
                </span>
              </span>
              {worldSelected && (
                <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          )}

          <p className="text-neutral-2-40 text-xs">
            Location data provided by Google. Place names and boundaries reflect Google's data and do not represent our views or political opinions.
          </p>
        </>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="coord-lat" className="text-neutral-2-60 block text-xs">
                Latitude
              </label>
              <input
                type="text"
                id="coord-lat"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                disabled={disabled || coordsLoading}
                placeholder="e.g. 28.6139"
                className="border-neutral-2-20 focus:border-secondary-1 focus:ring-secondary-1 mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-2-10"
              />
            </div>
            <div>
              <label htmlFor="coord-lon" className="text-neutral-2-60 block text-xs">
                Longitude
              </label>
              <input
                type="text"
                id="coord-lon"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
                disabled={disabled || coordsLoading}
                placeholder="e.g. 77.2090"
                className="border-neutral-2-20 focus:border-secondary-1 focus:ring-secondary-1 mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-2-10"
              />
            </div>
          </div>
          {coordsLoading && (
            <p className="text-secondary-1 flex items-center gap-2 text-xs">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Looking up location...
            </p>
          )}
          {coordsError && (
            <p className="text-red-600 text-xs">{coordsError}</p>
          )}
          <p className="text-neutral-2-60 text-xs">
            Enter GPS coordinates - city and country will be detected automatically
          </p>
          <p className="text-neutral-2-40 text-xs">
            Location data provided by Google. Place names and boundaries reflect Google's data and do not represent our views or political opinions.
          </p>
        </div>
      )}
    </div>
  );
}
